import { AxiosResponse, InternalAxiosRequestConfig } from "./types";
import { ElMessage } from "element-plus";
import { CanceledError } from "axios";
import qs from "qs";
import { SUCCESS_CODE, TRANSFORM_REQUEST_DATA } from "@/constants";
import { useUserStoreWithOut } from "@/store/modules/user";
import { useLocaleStoreWithOut } from "@/store/modules/locale";
import { objToFormData } from "@/utils";
import {
  applyRequestTimezoneTransform,
  applyRequestTimezoneTransformToFormData,
  applyResponseTimezoneTransform,
} from "@/utils/timezoneTransform";
import CryptoJS from "crypto-js";
import { i18n } from "@/plugins/vueI18n";

// 获取翻译函数的辅助函数
const getTranslation = (key: string): string => {
  if (i18n?.global) {
    try {
      const t = i18n.global.t as (key: string) => string;
      return t(key);
    } catch {
      return key;
    }
  }
  return key;
};

// MD5加密函数
const getmd5 = (str: string): string => {
  return CryptoJS.MD5(str).toString();
};

const sortObj = (obj: any) => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sortObj(item));
  }
  
  if (obj instanceof Date || obj instanceof RegExp) {
    return obj;
  }
  
  try {
    const keysArr = Object.keys(obj).sort();
    const sortedObj: any = {};
    for (const key of keysArr) {
      sortedObj[key] = sortObj(obj[key]);
    }
    return sortedObj;
  } catch (error) {
    console.error('排序对象时出错:', error);
    return obj;
  }
};

// 旧实现停用，统一使用下方带旧会话校验的 handleAuthExpired。
// const handleAuthExpired = () => {
//   const userStore = useUserStoreWithOut();
//   userStore.reset();
//   window.location.reload();
// };

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  const userStore = useUserStoreWithOut();
  const isFormDataPayload =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  // 检查是否需要登录验证（排除登录和验证码接口）
  const url = config.url?.replace(config.baseURL || "", "") || "";
  if (config.params && typeof config.params === "object") {
    applyRequestTimezoneTransform(config.params, url, config.baseURL);
  }

  if (config.data && typeof config.data === "object") {
    if (isFormDataPayload) {
      applyRequestTimezoneTransformToFormData(config.data as FormData, url, config.baseURL);
    } else {
      applyRequestTimezoneTransform(config.data, url, config.baseURL);
    }
  }

  if (
    url !== "/login/login" &&
    url !== "/recaptcha/siteverify" &&
    url !== "/auth/getAuth" &&
    (!userStore.getToken ||
      userStore.getToken.trim() === "" ||
      !userStore.getUserInfo)
  ) {
    // 如果没有用户信息，重定向到登录页
    window.location.href = "/login";
    return config;
  }

  // 如果有token，添加认证和签名
  if (userStore.getToken) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = userStore.getToken;

    // 设置语言
    let lang = "zh_CN";
    // 获取当前语言设置
    try {
      const localeStore = useLocaleStoreWithOut();
      const currentLocale = localeStore.getCurrentLocale;
      if (currentLocale && currentLocale.lang) {
        // 前端和后端使用相同的语言代码格式
        lang = currentLocale.lang;
      }
    } catch (error) {
      console.warn(getTranslation("common.http.getLocaleFailed"), error);
    }

    if (config.data && typeof config.data === "object") {
      if (isFormDataPayload) {
        const timestamp = String(new Date().getTime());
        const sendData: Record<string, any> = {};
        (config.data as FormData).forEach((value, key) => {
          if (!(value instanceof File)) {
            sendData[key] = value;
          }
        });
        sendData.langue = lang;
        sendData.timestamp = timestamp;
        sendData.signType = "MD5";

        const sortedData = sortObj(sendData);
        let stringA = "";
        for (const key in sortedData) {
          if (
            sortedData[key] !== "undefined" &&
            sortedData[key] !== "" &&
            sortedData[key] !== null &&
            key !== "sign" &&
            sortedData[key] !== undefined
          ) {
            let str = sortedData[key];
            if (typeof sortedData[key] === "object") {
              str = JSON.stringify(sortedData[key]);
            }
            stringA += key + "=" + str + "&";
          }
        }
        const stringSignTemp =
          stringA + "key=" + (import.meta.env.VITE_MD5_KEY || "default_key");
        const sign = getmd5(stringSignTemp).toUpperCase();

        (config.data as FormData).append("langue", lang);
        (config.data as FormData).append("timestamp", timestamp);
        (config.data as FormData).append("signType", "MD5");
        (config.data as FormData).append("sign", sign);
      } else {
        config.data.langue = lang;
        config.data.timestamp = new Date().getTime();
        config.data.signType = "MD5";

        // 生成签名
        const sendData = sortObj(config.data);
        let stringA = "";
        for (const key in sendData) {
          if (
            sendData[key] !== "undefined" &&
            sendData[key] !== "" &&
            sendData[key] !== null &&
            key !== "sign" &&
            sendData[key] !== undefined
          ) {
            let str = sendData[key];
            if (typeof sendData[key] === "object") {
              str = JSON.stringify(sendData[key]);
            }
            stringA += key + "=" + str + "&";
          }
        }
        const stringSignTemp =
          stringA + "key=" + (import.meta.env.VITE_MD5_KEY || "default_key");
        const sign = getmd5(stringSignTemp).toUpperCase();
        config.data.sign = sign;
      }
    }
  }

  // 处理其他数据格式
  if (
    config.method === "post" &&
    config.headers["Content-Type"] === "application/x-www-form-urlencoded"
  ) {
    config.data = qs.stringify(config.data);
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === "post" &&
    config.headers["Content-Type"] === "multipart/form-data"
  ) {
    if (!isFormDataPayload) {
      config.data = objToFormData(config.data);
    }
  }
  if (config.method === "get" && config.params) {
    let url = config.url as string;
    url += "?";
    const keys = Object.keys(config.params);
    for (const key of keys) {
      if (config.params[key] !== void 0 && config.params[key] !== null) {
        url += `${key}=${encodeURIComponent(config.params[key])}&`;
      }
    }
    url = url.substring(0, url.length - 1);
    config.params = {};
    config.url = url;
  }
  return config;
};

const isStaleSessionResponse = (config?: InternalAxiosRequestConfig) => {
  const token = config?.headers?.Authorization;
  return !!token && token !== useUserStoreWithOut().getToken;
};

const handleAuthExpired = (config?: InternalAxiosRequestConfig) => {
  if (isStaleSessionResponse(config)) return;
  useUserStoreWithOut().expireSession();
};

const defaultResponseInterceptors = async (response: AxiosResponse) => {
  if (isStaleSessionResponse(response.config)) {
    throw new CanceledError("Response belongs to an inactive session");
  }
  let res = response.data;

  // 处理图片key
  if (response.headers["image_key"] !== undefined) {
    sessionStorage.setItem("image_key", response.headers["image_key"]);
    return response.data;
  }

  // 处理文件流
  if (response.config.responseType === "blob") {
    if (!(res instanceof Blob) || !res.type.includes("json")) return res;
    const payload = JSON.parse(await res.text());
    if (isStaleSessionResponse(response.config)) {
      throw new CanceledError("Response belongs to an inactive session");
    }
    if (!payload.verify && payload.code === undefined) return res;
    res = payload;
  }
  const responseTimezone = res?.data?.oper?.timezone;

  // 处理Vue2项目的响应格式 { verify: { sign: 'success', desc: '' }, data: {} }
  if (res.verify) {
    if (res.verify.sign === "success") {
      // 成功响应，从响应头获取token（与Vue2项目保持一致）
      if (res.data instanceof Object && response.headers.authorization) {
        res.data.token = response.headers.authorization;
      }
      applyResponseTimezoneTransform(
        res.data,
        response.config?.url,
        response.config?.baseURL,
        responseTimezone,
      );
      // 返回data部分
      return {
        code: 0,
        data: res.data,
        message: res.verify.desc || "success",
      };
    } else {
      // 错误响应
      const errorMessage =
        res.verify.desc || getTranslation("common.http.requestFailed");

      // 处理会话超时和权限错误
      if (res.verify.sign === "sessionTimeOut" || String(res.verify.sign) === "401") {
        handleAuthExpired(response.config);
      } else {
        // 3000 is a signature error; no_auth/403/5102 are permission errors.
        ElMessage.error(errorMessage);
      }

      return Promise.reject(new Error(res.verify.desc || "Error"));
    }
  }

  // 处理标准响应格式 { code: 0, data: {}, message: '' }
  if (res.code === SUCCESS_CODE) {
    applyResponseTimezoneTransform(
      res.data,
      response.config?.url,
      response.config?.baseURL,
      responseTimezone,
    );
    return res;
  } else {
    // 显示错误消息
    const errorMessage =
      res.message || res.msg || getTranslation("common.http.requestFailed");

    // 处理401未授权
    if (String(res.code) === "401" || response?.status === 401) {
      handleAuthExpired(response.config);
    } else {
      ElMessage.error(errorMessage);
    }

    // 返回Promise.reject以便上层处理
    return Promise.reject(res);
  }
};

export {
  defaultResponseInterceptors,
  defaultRequestInterceptors,
  handleAuthExpired,
  isStaleSessionResponse,
};
